import React, { useMemo } from 'react';
import { Box, Typography, IconButton, Paper, Grid } from '@mui/material';
import { ChevronLeft, ChevronRight, FiberManualRecord } from '@mui/icons-material';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  addMonths,
  subMonths,
  isToday,
  parseISO,
} from 'date-fns';
import { uk } from 'date-fns/locale';

interface CustomMonthCalendarProps {
  currentMonth: string; // YYYY-MM format
  onMonthChange: (month: string) => void;
  selectedDate: Date | null;
  onDateChange: (date: Date | null) => void;
  highlightedDates: string[]; // Array of YYYY-MM-DD strings for dates with "кружки"
  disabledDates?: string[]; // Array of YYYY-MM-DD strings for disabled dates
}

const CustomMonthCalendar: React.FC<CustomMonthCalendarProps> = ({
  currentMonth,
  onMonthChange,
  selectedDate,
  onDateChange,
  highlightedDates,
  disabledDates = [],
}) => {
  const displayMonth = useMemo(() => parseISO(`${currentMonth}-01`), [currentMonth]);

  const days = useMemo(() => {
    const firstDay = startOfMonth(displayMonth);
    const lastDay = endOfMonth(displayMonth);
    return eachDayOfInterval({ start: firstDay, end: lastDay });
  }, [displayMonth]);

  const startDayIndex = (startOfMonth(displayMonth).getDay() + 6) % 7; // Monday start

  return (
    <Paper variant="outlined" sx={{ p: 2, maxWidth: 350, mx: 'auto', borderRadius: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <IconButton size="small" onClick={() => onMonthChange(format(subMonths(displayMonth, 1), 'yyyy-MM'))}>
          <ChevronLeft />
        </IconButton>
        <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
          {format(displayMonth, 'MMMM yyyy', { locale: uk })}
        </Typography>
        <IconButton size="small" onClick={() => onMonthChange(format(addMonths(displayMonth, 1), 'yyyy-MM'))}>
          <ChevronRight />
        </IconButton>
      </Box>

      <Grid container textAlign="center" mb={1}>
        {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'].map(d => (
          <Grid item xs={1.714} key={d}><Typography variant="caption" fontWeight="bold">{d}</Typography></Grid>
        ))}
      </Grid>

      <Grid container>
        {[...Array(startDayIndex)].map((_, i) => <Grid item xs={1.714} key={`empty-${i}`} />)}
        {days.map(d => {
          const ds = format(d, 'yyyy-MM-dd');
          const isSelected = selectedDate && isSameDay(d, selectedDate);
          const isHighlighted = highlightedDates.includes(ds);
          const isDisabled = disabledDates.includes(ds);
          
          return (
            <Grid item xs={1.714} key={ds} display="flex" justifyContent="center" mb={1}>
              <Box
                onClick={() => !isDisabled && onDateChange(d)}
                sx={{
                  width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '50%', cursor: isDisabled ? 'default' : 'pointer',
                  bgcolor: isSelected ? 'primary.main' : (isToday(d) ? 'action.selected' : 'transparent'),
                  color: isSelected ? 'white' : (isDisabled ? 'action.disabled' : 'text.primary'),
                  '&:hover': !isDisabled ? { bgcolor: isSelected ? 'primary.dark' : 'action.hover' } : {},
                  position: 'relative'
                }}
              >
                {format(d, 'd')}
                {isHighlighted && (
                  <FiberManualRecord sx={{ fontSize: 8, color: 'success.main', position: 'absolute', bottom: 4 }} />
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
};

export default CustomMonthCalendar;
